/**
 * useScreenTheme — Web
 *
 * Returns a merged screen-theme object:
 *   base  = screenThemes[screenName]['light']   (existing static colors)
 *   brand = resolveClientOverrides(tokens, key) (brand-colored property overrides)
 *   result = { ...base, ...brand[screenName] }
 *
 * Switching theme is a single Redux dispatch:
 *   dispatch(setClientTheme('red'))
 * All components using this hook re-render automatically with the new brand colors.
 */
import { useSelector } from 'react-redux';
import { screenThemes } from '../theme/screenThemes';
import {
  getClientThemeTokens,
  resolveClientOverrides,
  applyClientOverride,
} from '../theme/clientThemes';
import type { RootState } from '../store';

export const useScreenTheme = <T extends keyof typeof screenThemes>(
  screenName: T,
): (typeof screenThemes)[T]['light' | 'dark'] => {
  // Web always uses light mode for now
  const mode = 'light';

  // Read active theme name from Redux (defaults to 'blue' on first load)
  const themeName = useSelector(
    (state: RootState) => state.theme?.themeName ?? 'blue',
  );

  // 1. Static base colors for this screen
  const base = screenThemes[screenName][mode];

  // 2. Brand-specific overrides (cached per theme+mode)
  const tokens = getClientThemeTokens(themeName);
  const overrides = resolveClientOverrides(tokens, `${themeName}:${mode}`);

  // 3. Shallow-merge: only brand-colored keys are replaced
  return applyClientOverride(
    base as Record<string, string>,
    overrides[screenName as string],
  ) as (typeof screenThemes)[T]['light' | 'dark'];
};